import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PracticeActivityQuery } from '@examdojo/practice-activity';
import {
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptRealtimeService,
} from '@examdojo/question';
import { SolutionCaptureService } from '@examdojo/solution';
import { concatMap, debounceTime, distinctUntilChanged, filter, finalize, map, merge, Observable, of, tap } from 'rxjs';

@Injectable()
export class CloseSolutionCaptureDialogEffectService {
  constructor(
    private readonly solutionCaptureService: SolutionCaptureService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly questionAttemptRealtimeService: QuestionAttemptRealtimeService,
  ) {
    merge(
      this.shouldCloseSolutionCaptureDialogOnAttemptStatusChange(),
      this.shouldCloseSolutionCaptureDialogOnActivityChange(),
      this.sendCustomDismissEventOnDismissOfSolutionCapture(),
    )
      .pipe(
        finalize(() => {
          if (this.solutionCaptureService.isDialogOpen()) {
            this.solutionCaptureService.dismiss();
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly activePracticeActivityId$: Observable<string | undefined> = this.practiceActivityQuery.ongoingActivityId$;

  shouldCloseSolutionCaptureDialogOnAttemptStatusChange() {
    return this.questionAttemptQuery.attemptStatus$.pipe(
      distinctUntilChanged(),
      filter((status) => !!status && status !== 'PENDING'),
      tap((status) => {
        if (status === 'GRADED' || status === 'FAILED') {
          this.questionAttemptService.setOpenCaptureDialogFlag(false);
          this.solutionCaptureService.dismiss();
        }
      }),
    );
  }

  shouldCloseSolutionCaptureDialogOnActivityChange() {
    return this.activePracticeActivityId$.pipe(
      distinctUntilChanged(),
      tap((activePracticeActivityId) => {
        if (!activePracticeActivityId) {
          this.questionAttemptService.setOpenCaptureDialogFlag(false);
          this.solutionCaptureService.dismiss();
        }
      }),
    );
  }
  private sendCustomDismissEventOnDismissOfSolutionCapture() {
    return merge(
      this.solutionCaptureService.dialogDismissed$.pipe(map(() => ({ dismissed: true }))),
      this.solutionCaptureService.dialogOpened$.pipe(map(() => ({ opened: true }))),
    ).pipe(
      debounceTime(250),
      distinctUntilChanged(),
      concatMap((event) => {
        if ('dismissed' in event && event.dismissed) {
          return this.questionAttemptRealtimeService.sendQuestionSolutionCaptureDialogEvent(false);
        }

        if ('opened' in event && event.opened) {
          return this.questionAttemptRealtimeService.sendQuestionSolutionCaptureDialogEvent(true);
        }

        return of(null);
      }),
    );
  }
}
